import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import Icon from './icon';
import ArrowLeftIcon from '~icons/lucide/arrow-left';
import tw from '../helpers/tw';

interface MobileHeaderSignature {
  Args: {
    title?: string | null;
    noSpacer?: boolean;
    scrollOpacity?: boolean;
    back?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class MobileHeader extends Component<MobileHeaderSignature> {
  @tracked isHidden = false;
  @tracked scrollY = 0;

  private lastScrollY = 0;
  private readonly handleScroll: () => void;

  constructor(owner: Owner, args: MobileHeaderSignature['Args']) {
    super(owner, args);

    this.lastScrollY = window.scrollY;
    this.scrollY = window.scrollY;

    this.handleScroll = () => {
      const currentY = window.scrollY;

      if (this.args.scrollOpacity) {
        this.scrollY = currentY;
        return;
      }

      // Don't hide if at the top of the page
      if (currentY < 50) {
        this.isHidden = false;
        this.lastScrollY = currentY;
        return;
      }

      const delta = currentY - this.lastScrollY;

      if (delta > 10 && !this.isHidden) {
        this.isHidden = true;
      } else if (delta < -5 && this.isHidden) {
        this.isHidden = false;
      }

      this.lastScrollY = currentY;
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    registerDestructor(this, () => {
      window.removeEventListener('scroll', this.handleScroll);
    });
  }

  get bgOpacity(): number {
    return Math.min(this.scrollY / 80, 1);
  }

  get backgroundStyle(): string {
    if (!this.args.scrollOpacity) return '';
    return `opacity: ${this.bgOpacity}`;
  }

  get titleStyle(): string {
    if (!this.args.scrollOpacity) return '';
    return `opacity: ${this.bgOpacity}`;
  }

  get shouldHide(): boolean {
    return !this.args.scrollOpacity && this.isHidden;
  }

  @action
  goBack(): void {
    window.history.back();
  }

  <template>
    <div
      class={{tw
        "fixed top-0 right-0 left-0 z-40 lg:hidden"
        "transition-transform duration-300"
        (if this.shouldHide "-translate-y-full")
      }}
    >
      {{! Background layer — fades in on scroll when @scrollOpacity is true }}
      <div
        class="bg-base-100/80 border-base-300 absolute inset-0 border-b backdrop-blur-sm"
        style={{this.backgroundStyle}}
      ></div>

      {{! Content layer — always fully opaque }}
      {{! Safe-area spacer in its own div so py-3 below is not overridden }}
      <div class="relative">
        <div class="h-[env(safe-area-inset-top)]"></div>
        <div class="flex items-center px-4 py-3">
          {{#if @back}}
            <button
              type="button"
              class="btn btn-ghost btn-sm btn-circle -ml-2 shrink-0"
              aria-label="Go back"
              {{on "click" this.goBack}}
            >
              <Icon @icon={{ArrowLeftIcon}} class="h-5 w-5" />
            </button>
            {{#if @title}}
              <span
                class="ml-1 min-w-0 truncate text-base font-semibold"
                style={{this.titleStyle}}
              >{{@title}}</span>
            {{/if}}
          {{else if @title}}
            <h1 class="text-xl font-bold">{{@title}}</h1>
          {{/if}}
          {{yield}}
        </div>
      </div>
    </div>

    {{! Spacer to prevent content from being hidden behind fixed header }}
    {{#unless @noSpacer}}
      <div
        class="h-14 min-h-[calc(3.5rem+env(safe-area-inset-top))] lg:hidden"
      ></div>
    {{/unless}}
  </template>
}
